import { DashboardCalendarEventResponse, DashboardCalendarEventType } from "types";
import { DateUtils } from "utils";

export type CalendarEventVariant = "holiday" | "replacement" | "absence";

export interface CalendarDayDetails {
  background?: string;
  priority: number;
  tooltipLabels: string[];
}

const EVENT_VARIANTS_BY_TYPE: Record<DashboardCalendarEventType, CalendarEventVariant> = {
  HOLIDAY: "holiday",
  ABSENCE: "absence",
  COVERAGE: "replacement",
};

const EVENT_PRIORITIES: Record<CalendarEventVariant, number> = {
  holiday: 3,
  replacement: 2,
  absence: 1,
};

const EVENT_BACKGROUNDS: Record<CalendarEventVariant, string> = {
  holiday: "var(--mantine-color-violet-6)",
  replacement: "var(--mantine-color-orange-6)",
  absence: "var(--mantine-color-primary-6)",
};

const parseLocalDate = (value: string) => {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const getDateRange = (dateFrom: string, dateTo: string) => {
  const startDate = parseLocalDate(dateFrom);
  const endDate = parseLocalDate(dateTo);
  const result: string[] = [];

  for (
    const currentDate = new Date(startDate);
    currentDate <= endDate;
    currentDate.setDate(currentDate.getDate() + 1)
  ) {
    result.push(DateUtils.formatDateLocal(currentDate));
  }

  return result;
};

export const formatCalendarEventTooltip = (event: DashboardCalendarEventResponse) =>
  event.agentName ? `${event.title} - ${event.agentName}` : event.title;

export const buildCalendarDayDetails = (events: DashboardCalendarEventResponse[]) => {
  const map = new Map<string, CalendarDayDetails>();

  events.forEach((event) => {
    const variant = EVENT_VARIANTS_BY_TYPE[event.type];
    const priority = EVENT_PRIORITIES[variant];
    const background = EVENT_BACKGROUNDS[variant];
    const label = formatCalendarEventTooltip(event);

    getDateRange(event.dateFrom, event.dateTo).forEach((dateString) => {
      const details = map.get(dateString) ?? {
        background: undefined,
        priority: 0,
        tooltipLabels: [],
      };

      if (priority >= details.priority) {
        details.priority = priority;
        details.background = background;
      }

      if (!details.tooltipLabels.includes(label)) {
        details.tooltipLabels.push(label);
      }

      map.set(dateString, details);
    });
  });

  return map;
};
